/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: useFirewallServicesList.tsx
 ??? Created: 2025-01-XX
 ?? Description:
    Hook for managing Firewall Services list with DataGrid
----------------------------------------------------------------*/

import { useMemo, useState } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { fetchFirewallServices } from '../api/firewallServicesApi';
import type { FirewallService } from '../types';

export function useFirewallServicesList() {
  const { t } = useI18n();

  const availableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('firewallServices.columns.name'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: FirewallService) => (
          <span className="font-medium text-foreground">{item.name}</span>
        ),
      },
      {
        key: 'protocol',
        label: t('firewallServices.columns.protocol'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: FirewallService) => (
          <span className="text-sm text-muted-foreground uppercase">{item.protocol}</span>
        ),
      },
      {
        key: 'port',
        label: t('firewallServices.columns.port'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: FirewallService) => (
          <span className="text-sm text-foreground font-mono">{item.port}</span>
        ),
      },
      {
        key: 'description',
        label: t('firewallServices.columns.description'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: FirewallService) => (
          <span className="text-sm text-muted-foreground">
            {item.description || '-'}
          </span>
        ),
      },
      {
        key: 'enabled',
        label: t('firewallServices.columns.status'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: FirewallService) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
              item.enabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
            }`}
          >
            {item.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        ),
      },
      {
        key: 'actions',
        label: t('common.actions'),
        sortable: false,
        visible: true,
      },
    ],
    [t]
  );

  const fetchData = useMemo(
    () => async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      const result = await fetchFirewallServices({
        ...params,
      });

      return {
        data: result.data,
        pagination: result.pagination,
      };
    },
    []
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'firewallServices',
  });

  const services = useMemo(() => data as FirewallService[], [data]);

  return {
    services,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  };
}
